import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ContentCopyRounded as CopyIcon,
  DownloadRounded as DownloadIcon,
} from "@mui/icons-material";
import { TOUCH_TARGET } from "../../theme/theme";
import {
  CONTENT_POST_CAPTION_MAX,
  CONTENT_POST_PLATFORMS,
  type ContentMarkPostedInput,
  type ContentPostPlatform,
  type ContentReviewItem,
} from "../../services/contentReviewService";
import { beatLines } from "./sceneBeats";

/**
 * PKT-0612 slice 1 — "Mark as posted".
 *
 * POSTING IS MANUAL BY STANDING RULING. This sheet does not post anything and
 * cannot: it has no network call of its own. It exists so the act of posting
 * and the act of RECORDING it are the same two taps — open, record — instead
 * of a post now and a Drive ledger row from memory later (PKT-0586's file,
 * which is still empty).
 *
 * What it puts in the human's hand:
 *   · the reel (Download — the same signed-link path as the card's button);
 *   · a caption to paste, with a copy button. 🔴 THE MIRROR CARRIES NO POST
 *     CAPTION TODAY (measured 2026-09-02: content_review_items has subject,
 *     template and per-beat on-screen captions from migration 089; no post
 *     caption, first comment or hashtags — PKT-0612 scope item 1 is the DTO
 *     change that adds them). So the pre-fill is the on-screen copy, labelled
 *     as such, and the field is editable: what is stored is what was POSTED.
 *   · platform (Instagram by default) and an optional post URL.
 *
 * What it never asks for: who posted. `posted_by` is the session user, written
 * by the server; the request body cannot carry it (422 if it tries).
 */

interface Props {
  item: ContentReviewItem | null;
  busy: boolean;
  onConfirm: (item: ContentReviewItem, input: ContentMarkPostedInput) => void;
  onDownload: (item: ContentReviewItem) => void;
  onCancel: () => void;
}

const PLATFORM_LABEL: Record<ContentPostPlatform, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  facebook: "Facebook",
  other: "Somewhere else",
};

/** PKT-0631 · the literal state the sheet shows when no post kit crossed the mirror. */
export const POST_KIT_UNKNOWN = "UNKNOWN — no post kit crossed the mirror for this reel.";

/**
 * The best caption the product can offer. PKT-0631: when the box's post kit
 * crossed the mirror (migration 096), that caption VERBATIM — the box already
 * ran it through the brand + fair-housing guards. Otherwise the pre-096
 * fallback: the reel's on-screen copy, labelled as a stand-in.
 */
export const suggestedCaption = (item: ContentReviewItem): string => {
  if (item.post_kit?.caption && item.post_kit.caption.trim().length > 0) {
    return item.post_kit.caption.slice(0, CONTENT_POST_CAPTION_MAX);
  }
  // Same reel order as the beats panel (sceneBeats.ts): placed scenes by
  // offset, unplaced after them. Captionless beats contribute nothing.
  const ordered = beatLines(item.scenes).map((line) => line.id);
  const byId = new Map((item.scenes ?? []).map((scene) => [scene.id, scene]));
  const beats = ordered
    .map((id) => (byId.get(id)?.caption ?? "").trim())
    .filter((caption) => caption.length > 0);
  return [item.subject, ...beats].filter(Boolean).join("\n").slice(0, CONTENT_POST_CAPTION_MAX);
};

const ContentMarkPostedDialog: React.FC<Props> = ({ item, busy, onConfirm, onDownload, onCancel }) => {
  const [platform, setPlatform] = React.useState<ContentPostPlatform>("instagram");
  const [postUrl, setPostUrl] = React.useState("");
  const [caption, setCaption] = React.useState("");
  const [copied, setCopied] = React.useState<"idle" | "copied" | "failed">("idle");

  // Reset per reel: the sheet is reused across cards and a stale caption from
  // the previous reel is exactly the kind of quiet lie a ledger must not take.
  React.useEffect(() => {
    if (!item) return;
    setPlatform("instagram");
    setPostUrl("");
    setCaption(suggestedCaption(item));
    setCopied("idle");
  }, [item]);

  const urlInvalid = postUrl.trim().length > 0 && !/^https:\/\//.test(postUrl.trim());

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* the text is on screen; selecting it is the fallback */
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied("copied");
    } catch {
      setCopied("failed");
    }
  };

  return (
    <Dialog open={Boolean(item)} onClose={() => !busy && onCancel()} fullWidth maxWidth="sm" aria-labelledby="mark-posted-title">
      <DialogTitle id="mark-posted-title">Mark as posted</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {item?.subject ?? ""} — post it in the app yourself, then record it here so the ledger
            moves. Nothing is published from this screen.
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => item && onDownload(item)}
              disabled={!item?.video_available || busy}
              sx={{ minHeight: TOUCH_TARGET }}
            >
              Download reel
            </Button>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => void copyCaption()}
              disabled={!caption.trim() || busy}
              sx={{ minHeight: TOUCH_TARGET }}
            >
              {copied === "copied" ? "Caption copied" : copied === "failed" ? "Copy failed — select it" : "Copy caption"}
            </Button>
          </Stack>

          {/* PKT-0631 · THE POST KIT, READ-ONLY (§C option 1). What the box wrote,
              shown so the human can paste it; nothing here edits it, so nothing
              reaches the clipboard that the box's propertyLeak / priceInCaption
              guards did not see. Absent kit = the literal UNKNOWN, never "". */}
          {item?.post_kit ? (
            <Stack spacing={0.75} data-testid="content-post-kit">
              <Typography variant="caption" color="text.secondary">Post kit from the box (read-only)</Typography>
              <Typography variant="body2" component="pre" data-testid="content-post-kit-first-comment"
                sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0 }}>
                {item.post_kit.first_comment}
              </Typography>
              <Typography variant="caption" color="text.primary" data-testid="content-post-kit-hashtags">
                {item.post_kit.hashtags.length > 0
                  ? item.post_kit.hashtags.map((tag) => `#${tag.replace(/^#/, "")}`).join(" ")
                  : "No hashtags in the kit."}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<CopyIcon />}
                onClick={() => void copyText(item.post_kit!.first_comment)}
                disabled={!item.post_kit.first_comment.trim() || busy}
                sx={{ alignSelf: "flex-start" }}
              >
                Copy first comment
              </Button>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.disabled" data-testid="content-post-kit-unknown">
              {POST_KIT_UNKNOWN}
            </Typography>
          )}

          <TextField
            label="Caption as posted"
            helperText={
              item?.post_kit?.caption
                ? "Pre-filled from the post kit the box wrote. Edit only to match what you actually posted."
                : item?.scenes?.length
                  ? "Pre-filled from the reel's on-screen copy — no post caption crossed the mirror yet. Edit to match what you posted."
                  : "No caption on file for this reel. Type what you posted."
            }
            value={caption}
            onChange={(event) => setCaption(event.target.value.slice(0, CONTENT_POST_CAPTION_MAX))}
            multiline
            minRows={3}
            fullWidth
            disabled={busy}
            inputProps={{ maxLength: CONTENT_POST_CAPTION_MAX, "aria-label": "Caption as posted" }}
          />

          <TextField
            select
            label="Platform"
            value={platform}
            onChange={(event) => setPlatform(event.target.value as ContentPostPlatform)}
            disabled={busy}
            inputProps={{ "aria-label": "Platform" }}
          >
            {CONTENT_POST_PLATFORMS.map((value) => (
              <MenuItem key={value} value={value}>{PLATFORM_LABEL[value]}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Post link (optional)"
            placeholder="https://www.instagram.com/reel/…"
            value={postUrl}
            onChange={(event) => setPostUrl(event.target.value.slice(0, 500))}
            error={urlInvalid}
            helperText={urlInvalid ? "Needs to start with https://" : " "}
            fullWidth
            disabled={busy}
            inputProps={{ "aria-label": "Post link" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} sx={{ minHeight: TOUCH_TARGET }}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disabled={!item || busy || urlInvalid}
          onClick={() => item && onConfirm(item, {
            platform,
            post_url: postUrl.trim() || null,
            caption_as_posted: caption,
          })}
          sx={{ minHeight: TOUCH_TARGET }}
        >
          Record post
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentMarkPostedDialog;
